import {
    Children,
    cloneElement,
    createContext,
    forwardRef,
    isValidElement,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactElement,
    type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronDown, XIcon } from "lucide-react";
import { Button } from "../../../ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import Tooltip from "../../Tooltip";
import { useVisibleTagCount } from "./hooks/useVisibleTagCount";
import { getNodeText } from "./helpers";
import { cn } from "../../../../lib/utils";
import type { GroupProps, ItemProps, SelectContextValue, SelectProps } from "./types";

const SelectContext = createContext<SelectContextValue | null>(null);

const Tag = forwardRef<HTMLSpanElement, { label: string; onRemove: () => void }>(
    ({ label, onRemove }, ref) => (
        <span
            ref={ref}
            className="tw-flex tw-min-w-0 tw-shrink tw-justify-between tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-input tw-bg-background tw-py-0.5 tw-pr-1 tw-pl-2.5 tw-text-xs tw-font-medium tw-text-foreground tw-shadow-xs"
        >
            <span className="tw-min-w-0 tw-flex-1 tw-truncate">{label}</span>
            <span
                role="button"
                aria-label={`Remove ${label}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                className="tw-shrink-0 tw-rounded-full tw-p-0.5 tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-accent-foreground"
            >
                <XIcon className="tw-size-2.5" />
            </span>
        </span>
    )
);

function Group({ heading, children }: GroupProps) {
    return (
        <CommandGroup heading={heading} className="[&_[cmdk-group-heading]]:tw-px-1.5 [&_[cmdk-group-heading]]:tw-py-1.5">
            {children}
        </CommandGroup>
    );
}

const isGroupElement = (child: ReactNode): child is ReactElement<GroupProps> =>
    isValidElement(child) && child.type === Group;

function Item<T extends string = string>({ value, children }: ItemProps<T>) {
    const context = useContext(SelectContext);
    if (!context) return null;

    const { values, onSelect } = context;
    const checked = values.includes(value);

    return (
        <CommandItem
            data-slot="select-item"
            data-checked={checked}
            value={value}
            onSelect={() => onSelect(value)}
            className="tw-mb-0.5 tw-flex tw-w-full tw-gap-1.5 tw-py-1 tw-pr-2 tw-pl-1.5 tw-text-sm hover:tw-bg-accent hover:tw-text-accent-foreground data-[checked=true]:tw-bg-accent/50"
        >
            <span className="tw-min-w-0 tw-flex-1 tw-truncate">{children}</span>
            <span className="tw-flex tw-size-4 tw-shrink-0 tw-items-center tw-justify-center">
                {checked && <CheckIcon className="tw-size-4" />}
            </span>
        </CommandItem>
    );
}

function Select<T extends string = string>({
    children,
    className,
    style,
    value,
    onChange,
    placeholder = "Select...",
    showSearch = true,
    mode = "single",
    allowClear = false,
    disabled = false,
    searchValue,
    onSearchChange,
}: SelectProps<T>) {
    const { t } = useTranslation();
    const [internalSearch, setInternalSearch] = useState<string>("");
    const [open, setOpen] = useState(false);

    const isSearchControlled = onSearchChange !== undefined;
    const search = isSearchControlled ? (searchValue ?? "") : internalSearch;
    const setSearch = isSearchControlled ? onSearchChange : setInternalSearch;

    const values = useMemo(() => {
        if (value == null || value === "") return [];
        return Array.isArray(value) ? value : [value];
    }, [value]);

    const labels = useMemo(() => {
        const map = new Map<string, string>();
        const walk = (nodes: ReactNode) => {
            Children.forEach(nodes, (child) => {
                if (!isValidElement(child)) return;
                if (isGroupElement(child)) {
                    walk(child.props.children);
                } else if (isValidElement<{ value: string; children?: ReactNode }>(child)) {
                    map.set(child.props.value, getNodeText(child.props.children));
                }
            });
        };
        walk(children);
        return map;
    }, [children]);

    const visibleChildren = useMemo(() => {
        if (isSearchControlled || !showSearch || !search.trim()) return children;
        const query = search.trim().toLowerCase();

        const filterNodes = (nodes: ReactNode): ReactNode[] =>
            Children.toArray(nodes).flatMap((child) => {
                if (!isValidElement(child)) return [child];
                if (isGroupElement(child)) {
                    const filteredChildren = filterNodes(child.props.children);
                    return filteredChildren.length > 0 ? [cloneElement(child, undefined, filteredChildren)] : [];
                }
                if (isValidElement<{ children?: ReactNode }>(child) && getNodeText(child.props.children).toLowerCase().includes(query)) {
                    return [child];
                }
                return [];
            });

        return filterNodes(children);
    }, [children, showSearch, search, isSearchControlled]);

    // Consecutive ungrouped items are bundled into a single CommandGroup (matching prior
    // behavior for callers with no Select.Group), while Select.Group elements pass through
    // as their own CommandGroup so they get their own heading.
    const renderedChildren = useMemo(() => {
        const nodes = Children.toArray(visibleChildren);
        const output: ReactNode[] = [];
        let buffer: ReactNode[] = [];

        const flushBuffer = () => {
            if (buffer.length > 0) {
                output.push(<CommandGroup key={`group-${output.length}`}>{buffer}</CommandGroup>);
                buffer = [];
            }
        };

        nodes.forEach((node) => {
            if (isGroupElement(node)) {
                flushBuffer();
                output.push(node);
            } else {
                buffer.push(node);
            }
        });
        flushBuffer();

        return output;
    }, [visibleChildren]);

    const handleSelect = useCallback((itemValue: string) => {
        const typedValue = itemValue as T;
        if (mode === "single") {
            (onChange as ((value?: T) => void) | undefined)?.(typedValue);
            setOpen(false);
            return;
        }
        const next = values.includes(typedValue)
            ? values.filter((v) => v !== typedValue)
            : [...values, typedValue];
        (onChange as ((value?: T[]) => void) | undefined)?.(next as T[]);
    }, [mode, onChange, values]);

    const onOpenChange = useCallback((next: boolean) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setSearch("");
    }, [disabled, setSearch]);

    const hasValue = values.length > 0;
    const { containerRef, firstTagRef, visibleCount } = useVisibleTagCount(values, labels);
    const hiddenValues = values.slice(visibleCount);

    return (
        <Popover open={open} onOpenChange={onOpenChange} modal>
            <PopoverTrigger
                data-slot="select-trigger"
                data-placeholder={!hasValue || undefined}
                disabled={disabled}
                style={style}
                className={cn(
                    "tw-relative tw-cursor-pointer tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-transparent tw-px-3 tw-py-1 tw-text-sm tw-shadow-sm tw-whitespace-nowrap tw-transition-colors tw-select-none focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-cursor-not-allowed disabled:tw-opacity-50 data-[placeholder]:tw-text-muted-foreground",
                    className
                )}
            >
                {mode === "multiple" && hasValue ? (
                    <span ref={containerRef} className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-items-center tw-gap-1 tw-overflow-hidden">
                        {values.slice(0, visibleCount).map((value, i) => (
                            <Tag
                                key={value}
                                ref={i === 0 ? firstTagRef : undefined}
                                label={labels.get(value) ?? value}
                                onRemove={() => handleSelect(value)}
                            />
                        ))}
                        {hiddenValues.length > 0 && (
                            <Tooltip
                                side="bottom"
                                align="start"
                                trigger={`+${hiddenValues.length} ${t("more")}`}
                                triggerClassName="tw-flex tw-shrink-0 tw-items-center tw-rounded-full tw-border tw-border-input tw-bg-background tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-foreground tw-shadow-xs"
                                contentClassName="tw-p-3 tw-bg-popover tw-text-popover-foreground tw-border tw-border-border tw-shadow-md"
                            >
                                <span className="tw-flex tw-max-w-52 tw-flex-col">
                                    {hiddenValues.map((value) => (
                                        <span key={value} className="tw-truncate tw-px-3 tw-py-1 tw-text-sm">
                                            {labels.get(value) ?? value}
                                        </span>
                                    ))}
                                </span>
                            </Tooltip>
                        )}
                    </span>
                ) : (
                    <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-left">
                        {hasValue ? (labels.get(values[0]) ?? values[0]) : placeholder}
                    </span>
                )}
                {allowClear && hasValue && (
                    <Button
                        type="button"
                        variant="ghost"
                        // size="xs"
                        className="tw-shrink-0 tw-rounded-sm hover:tw-bg-muted tw-cursor-pointer tw-p-1 tw-m-0"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (mode === "multiple") {
                                (onChange as ((value?: T[]) => void) | undefined)?.([]);
                            } else {
                                (onChange as ((value?: T | null) => void) | undefined)?.(null);
                            }
                        }}
                    >
                        <XIcon className="tw-size-3 tw-text-gray-600" />
                    </Button>
                )}
                <ChevronDown className="tw-pointer-events-none tw-size-4 tw-shrink-0 tw-text-muted-foreground" />
            </PopoverTrigger>

            <PopoverContent className="tw-w-(--radix-popover-trigger-width) tw-p-2 tw-max-w-xs" align="start">
                <Command shouldFilter={false}>
                    {showSearch && (
                        <CommandInput
                            autoFocus
                            placeholder="Search..."
                            {...(isSearchControlled
                                ? { onValueChange: setSearch }
                                : { value: search, onValueChange: setSearch })}
                        />
                    )}

                    <CommandList className="tw-max-h-64 tw-scroll-my-1">
                        <SelectContext.Provider value={{ mode, values, onSelect: handleSelect }}>
                            {renderedChildren}
                            <CommandEmpty className="tw-px-2 tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                                {t("no-items")}
                            </CommandEmpty>
                        </SelectContext.Provider>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

Select.Item = Item;
Select.Group = Group;

export default Select;
export { Item, Group };
